import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Save, ArrowLeft, Plus, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { getSection, putSection } from "@/lib/api";

// Field schema per section. Each entry: { key, label, type: 'text'|'textarea'|'array-string'|'array-object'|'array-string-lines'|'kv', fields? }
const SCHEMAS = {
    home: [
        { key: "name", label: "Name", type: "text" },
        { key: "role", label: "Role", type: "text" },
        { key: "currently", label: "Currently (institution)", type: "text" },
        { key: "identity", label: "One-line identity", type: "textarea" },
        { key: "tagline", label: "Tagline", type: "text" },
        { key: "qualifications", label: "Qualifications (one per line)", type: "array-string-lines" },
        { key: "featured", label: "Featured cards", type: "array-object", fields: [ { key: "kicker", label: "Kicker" }, { key: "title", label: "Title" }, { key: "href", label: "Link" } ] },
    ],
    about: [
        { key: "summary", label: "Summary (use blank lines for paragraphs)", type: "textarea", rows: 12 },
        { key: "academic_journey", label: "Academic Journey", type: "textarea", rows: 6 },
        { key: "industry_exposure", label: "Industry Exposure", type: "textarea", rows: 6 },
        { key: "industry_stints", label: "Industry stints (one per line)", type: "array-string-lines" },
        { key: "volunteering_intro", label: "Volunteering intro", type: "textarea", rows: 4 },
        { key: "volunteering", label: "Volunteering entries", type: "array-object", fields: [ { key: "title", label: "Title" }, { key: "body", label: "Body", type: "textarea" } ] },
        { key: "extracurricular", label: "Extracurricular", type: "textarea", rows: 4 },
    ],
    research: [
        { key: "agenda", label: "Research agenda", type: "textarea", rows: 6 },
        { key: "interests", label: "Interests (one per line)", type: "array-string-lines" },
        { key: "current_work", label: "Current work", type: "array-object", fields: [ { key: "title", label: "Title" }, { key: "summary", label: "Summary", type: "textarea" } ] },
    ],
    teaching: [
        { key: "philosophy", label: "Teaching philosophy", type: "textarea", rows: 6 },
        { key: "institute", label: "Primary institute", type: "text" },
        { key: "approach", label: "Teaching approach (one per line)", type: "array-string-lines" },
    ],
    cv: [
        { key: "summary", label: "CV summary", type: "textarea", rows: 5 },
        { key: "download_url", label: "CV PDF URL", type: "text" },
        { key: "timeline", label: "Timeline entries", type: "array-object", fields: [ { key: "date", label: "Date" }, { key: "title", label: "Title" }, { key: "institution", label: "Institution" }, { key: "body", label: "Body", type: "textarea" } ] },
    ],
    contact: [
        { key: "intro", label: "Intro", type: "textarea", rows: 3 },
        { key: "emails", label: "Emails (one per line)", type: "array-string-lines" },
        { key: "linkedin", label: "LinkedIn URL", type: "text" },
        { key: "google_scholar", label: "Google Scholar URL", type: "text" },
        { key: "orcid", label: "ORCID URL", type: "text" },
        { key: "ssrn", label: "SSRN URL", type: "text" },
    ],
};

const LABELS = {
    home: "Home",
    about: "About",
    research: "Research",
    teaching: "Teaching",
    cv: "CV",
    contact: "Contact",
};

export default function AdminContentEditor() {
    const { section } = useParams();
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setData(null);
        getSection(section).then((r) => setData(r?.data || {}));
    }, [section]);

    if (!data) return <p className="text-paper/70">Loading…</p>;
    const schema = SCHEMAS[section] || [];

    const setField = (key, val) => setData((d) => ({ ...d, [key]: val }));

    const save = async () => {
        setSaving(true);
        try {
            await putSection(section, data);
            toast.success("Saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save");
        } finally { setSaving(false); }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <Link to="/admin" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-paper/60 hover:text-paper"><ArrowLeft className="h-3 w-3" /> Back to dashboard</Link>
                    <h1 className="mt-1 font-display text-3xl sm:text-4xl">Edit · {LABELS[section] || section}</h1>
                </div>
                <Button onClick={save} disabled={saving} className="bg-paper text-ink hover:bg-paper/90" data-testid="admin-content-save-button">
                    <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
                </Button>
            </div>

            <Card className="p-6 rounded-2xl bg-white/[0.04] border-white/10">
                <div className="grid gap-6">
                    {schema.map((f) => (
                        <Field key={f.key} field={f} value={data[f.key]} onChange={(v) => setField(f.key, v)} />
                    ))}
                    {schema.length === 0 && (
                        <p className="text-sm text-paper/70">No editor defined for “{section}”.</p>
                    )}
                </div>
            </Card>
        </div>
    );
}

function Field({ field, value, onChange }) {
    if (field.type === "text") {
        return (
            <div className="space-y-1.5">
                <Label className="text-paper/80">{field.label}</Label>
                <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="bg-white/[0.05] border-white/10 text-paper" data-testid={`admin-field-${field.key}`} />
            </div>
        );
    }
    if (field.type === "textarea") {
        return (
            <div className="space-y-1.5">
                <Label className="text-paper/80">{field.label}</Label>
                <Textarea rows={field.rows || 5} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="bg-white/[0.05] border-white/10 text-paper" data-testid={`admin-field-${field.key}`} />
            </div>
        );
    }
    if (field.type === "array-string-lines") {
        const text = Array.isArray(value) ? value.join("\n") : (value || "");
        return (
            <div className="space-y-1.5">
                <Label className="text-paper/80">{field.label}</Label>
                <Textarea rows={5} value={text} onChange={(e) => onChange(e.target.value.split(/\n/).map((s) => s.trim()).filter(Boolean))} className="bg-white/[0.05] border-white/10 text-paper font-mono text-sm" data-testid={`admin-field-${field.key}`} />
            </div>
        );
    }
    if (field.type === "array-object") {
        const items = Array.isArray(value) ? value : [];
        return (
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <Label className="text-paper/80">{field.label}</Label>
                    <Button size="sm" variant="secondary" className="bg-white/[0.06] text-paper hover:bg-white/[0.1] border-white/10" onClick={() => onChange([...items, {}])} data-testid={`admin-field-${field.key}-add`}>
                        <Plus className="h-3.5 w-3.5 mr-1" /> Add
                    </Button>
                </div>
                <div className="space-y-3">
                    {items.map((it, idx) => (
                        <Card key={idx} className="p-4 rounded-xl bg-white/[0.03] border-white/10">
                            <div className="flex items-center justify-between mb-2">
                                <p className="font-mono text-[10px] uppercase tracking-widest text-paper/50">Item {idx + 1}</p>
                                <Button size="sm" variant="ghost" className="text-paper/70 hover:text-red-300" onClick={() => onChange(items.filter((_, i) => i !== idx))} data-testid={`admin-field-${field.key}-remove-${idx}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                            <div className="grid gap-3">
                                {field.fields.map((sf) => (
                                    <div key={sf.key} className="space-y-1">
                                        <Label className="text-paper/70 text-xs">{sf.label}</Label>
                                        {sf.type === "textarea" ? (
                                            <Textarea rows={3} value={it[sf.key] ?? ""} onChange={(e) => { const next = [...items]; next[idx] = { ...next[idx], [sf.key]: e.target.value }; onChange(next); }} className="bg-white/[0.05] border-white/10 text-paper" />
                                        ) : (
                                            <Input value={it[sf.key] ?? ""} onChange={(e) => { const next = [...items]; next[idx] = { ...next[idx], [sf.key]: e.target.value }; onChange(next); }} className="bg-white/[0.05] border-white/10 text-paper" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </Card>
                    ))}
                    {items.length === 0 && <p className="text-xs text-paper/50">No entries yet.</p>}
                </div>
            </div>
        );
    }
    return null;
}
